<template>
  <ak-field label="From Number" required>
    <ak-select
      ref="input"
      :model-value="selectedNumber"
      :options="numbers"
      :custom-label="makeLabel"
      required
      @select="setNumber"
    />
  </ak-field>
</template>

<script>
import { ref, computed, defineComponent, onMounted } from 'vue'
import Select from '@akerolabs/akero-vue/src/components/select/Select.vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-sms-form-select />
   */
  name: 'AkSmsFormSelect',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Select.name]: Select,
  },

  /**
   * @type {Boolean}
   * @description This prevents all element attributes that are not
   * defined in the props object from being applied to the rendered
   * component root element, the exception to this is "class" all
   * of the given classes would be merged with the component
   * defined ones.
   */
  inheritAttrs: false,

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Number|String}
     * @description The value of the select input
     */
    modelValue: {
      default: undefined,
      type: String,
    },

    /**
     * @type {Object}
     * @description The active account (drilled in or users)
     */
    activeAccount: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Object}
     * @description The active agency (drilled in or users)
     */
    activeAgency: {
      default: () => ({}),
      type: Object,
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['update:modelValue'],

  /**
   * @type {Function}
   * @returns {Object}
   * @description Returns the default state of the component.
   */
  setup(props, { emit }) {
    const numbers = ref([])
    const input = ref(null)

    const computedValue = computed({
      get() {
        return props.modelValue
      },
      set(value) {
        emit('update:modelValue', value)
      },
    })

    /**
     * @returns {Object}
     * @description Returns the selected mobile number from the array
     * of numbers.
     */
    const selectedNumber = computed(() => {
      return numbers.value.find((i) => i.msisdn === computedValue.value)
    })

    /**
     * @returns {Promise}
     * @description Fetches the mobile numbers from the API
     */
    const fetchNumbers = async () => {
      return request(`/accounts/${props.activeAccount._id}`, {
        params: {
          agency: props.activeAgency._id,
          overrideAccess: true,
          filters: 'contactDetails.prepaidSMSNo',
          per_page: 0,
        },
      })
    }

    const makeLabel = ({ msisdn, country }) => {
      return msisdn && country ? `${msisdn} - ${country}` : ''
    }

    const setNumber = ({ msisdn }) => {
      computedValue.value = msisdn
    }

    const validate = (force) => {
      return input.value.validate(force)
    }

    onMounted(async () => {
      const response = await fetchNumbers()
      const { prepaidSMSNo } = response.data.data.contactDetails
      numbers.value = prepaidSMSNo
    })

    return {
      numbers,
      computedValue,
      input,
      fetchNumbers,
      selectedNumber,
      makeLabel,
      setNumber,
      validate,
    }
  },

  // /**
  //  * @type {Object}
  //  * @description These methods return computed properties that can
  //  * be accessed like any other property on the components state
  //  */
  // computed: {
  //   /**
  //    * @type {Object}
  //    * @description Sets the get/set methods for the "computedValue"
  //    * property which is a String
  //    */
  //   computedValue: {
  //     /**
  //      * @description Gets the "computedValue", this returns the
  //      * "selectedValue" property
  //      */
  //     get() {
  //       return this.selectedValue
  //     },

  //     /**
  //      * @description Sets the "computedValue", this sets the
  //      * "selectedValue" property, and will run the validation if
  //      * required
  //      */
  //     set(value) {
  //       this.selectedValue = value
  //       this.$emit('input', value)
  //     },
  //   }
  // },

  // /**
  //  * @type {Object}
  //  * @description A collection of callback methods for watching the
  //  * state of the component.
  //  */
  // watch: {
  //   /**
  //    * @param {String}
  //    * @description Set internal value when prop change.
  //    */
  //   value(value) {
  //     this.selectedValue = value
  //   },
  // }
})
</script>
